import { useState } from 'react'
import { motion } from 'framer-motion'
import { AlertTriangle } from 'lucide-react'
import {
  type BudgetSummary,
  type BudgetPeriod,
  getBudgetStatus,
} from '../../lib/budgetCalculator'

interface Props {
  summary:              BudgetSummary | null
  budget:               number | null
  period:               BudgetPeriod
  lastMonthSpend:       number
  monthOverMonthDelta:  number | null
  memberCount?:         number
  onSetBudget:          (amount: number) => void
  onViewDetails:        () => void
}

// ─── BudgetCard ───────────────────────────────────────────────────────────────

export default function BudgetCard({
  summary,
  budget,
  period,
  lastMonthSpend,
  monthOverMonthDelta,
  memberCount = 1,
  onSetBudget,
  onViewDetails,
}: Props) {
  const [budgetInput, setBudgetInput] = useState('')
  const [editing,     setEditing]     = useState(false)
  const [editInput,   setEditInput]   = useState('')

  const suggestedBudget = memberCount <= 1 ? 200 : memberCount === 2 ? 300 : 450

  // ── No budget set / editing ───────────────────────────────────────────────

  if (!budget || editing) {
    return (
      <div className="bg-paper border border-border-sand rounded-3xl px-5 py-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
              Monthly budget
            </p>
            <p className="font-serif text-[15px] text-ink mt-0.5">
              {editing ? <>Change for <em className="italic">{period.label.toLowerCase()}</em></> : <>Set a <em className="italic">monthly</em> budget</>}
            </p>
          </div>
          {editing && (
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-[11px] uppercase tracking-[0.08em] font-medium text-ink-muted"
            >
              Cancel
            </button>
          )}
        </div>

        {!editing && (
          <p className="font-serif italic text-[12px] text-ink-muted leading-[1.5] mb-4">
            Track your grocery spend and we'll warn you before you go over.
          </p>
        )}

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 font-serif text-[14px] text-ink-muted">
              $
            </span>
            <input
              type="number"
              min="0"
              step="10"
              placeholder={String(suggestedBudget)}
              value={editing ? editInput : budgetInput}
              onChange={e => editing ? setEditInput(e.target.value) : setBudgetInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const val = parseFloat(editing ? editInput : budgetInput)
                  if (!isNaN(val) && val > 0) {
                    onSetBudget(val)
                    setEditing(false)
                  }
                }
              }}
              className="w-full pl-7 pr-4 py-2.5 rounded-2xl border border-border-sand bg-cream font-serif text-[14px] text-ink outline-none focus:border-forest"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              const raw = editing ? editInput : budgetInput
              const val = parseFloat(raw)
              if (!isNaN(val) && val > 0) {
                onSetBudget(val)
                setEditing(false)
              }
            }}
            className="rounded-2xl bg-forest text-forest-ink px-4 py-2.5 text-[13px] font-bold tracking-[-0.01em] shrink-0"
          >
            {editing ? 'Update' : 'Set'}
          </button>
        </div>

        {!editing && (
          <p className="font-serif italic text-[11px] text-ink-muted mt-2.5">
            Suggested ${suggestedBudget}/mo for your household.
          </p>
        )}
      </div>
    )
  }

  if (!summary) return null

  // ── Active budget ─────────────────────────────────────────────────────────

  const status   = getBudgetStatus(summary)
  const overBudget = summary.isOverBudget
  const nearLimit  = summary.isNearLimit

  const spentTone = overBudget ? 'text-terracotta' : nearLimit ? 'text-sand' : 'text-forest'
  const barClass  = overBudget ? 'bg-terracotta' : nearLimit ? 'bg-sand' : 'bg-forest'

  const momTone =
    monthOverMonthDelta === null ? 'text-ink-muted' :
    monthOverMonthDelta > 0       ? 'text-terracotta' :
    monthOverMonthDelta < 0       ? 'text-forest' :
                                    'text-ink-muted'

  const momLabel =
    monthOverMonthDelta === null ? '—' :
    monthOverMonthDelta > 0       ? `+$${monthOverMonthDelta.toFixed(2)}` :
    monthOverMonthDelta < 0       ? `−$${Math.abs(monthOverMonthDelta).toFixed(2)}` :
                                    '$0.00'

  return (
    <div className="bg-paper border border-border-sand rounded-3xl px-5 py-5">

      {/* Header */}
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted">
            {period.label}
          </p>
          <h3 className="font-serif text-[15px] text-ink mt-0.5">
            Monthly <em className="italic">budget</em>
          </h3>
        </div>
        <span className="text-[10px] uppercase tracking-[0.08em] font-medium" style={{ color: status.color }}>
          {status.label}
        </span>
      </div>

      {/* Spend display */}
      <div className="flex items-baseline gap-2 mb-1">
        <span className={`font-serif text-[36px] leading-none tracking-[-0.02em] tabular-nums ${spentTone}`}>
          ${summary.spent.toFixed(2)}
        </span>
        <span className="font-serif italic text-[13px] text-ink-muted">spent</span>
        <button
          type="button"
          onClick={() => { setEditInput(String(budget)); setEditing(true) }}
          className="ml-auto text-[10px] uppercase tracking-[0.08em] font-medium text-forest"
        >
          Edit
        </button>
      </div>
      <p className="font-serif italic text-[12px] text-ink-muted">
        of ${budget.toFixed(2)} budget · ${summary.remaining.toFixed(2)} remaining
      </p>

      {/* Progress bar */}
      <div className="my-4">
        <div className="h-2 bg-border-sand rounded-full overflow-hidden">
          <motion.div
            className={`h-full rounded-full ${barClass}`}
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(100, summary.percentUsed)}%` }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          />
        </div>
        <div className="flex justify-between mt-1.5">
          <span className="text-[10px] uppercase tracking-[0.08em] font-medium text-ink-muted tabular-nums">
            {Math.round(summary.percentUsed)}% used
          </span>
          <span className="text-[10px] uppercase tracking-[0.08em] font-medium text-ink-muted tabular-nums">
            {summary.daysLeft} day{summary.daysLeft !== 1 ? 's' : ''} left
          </span>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-cream-warm/40 rounded-xl px-3 py-2.5 text-center">
          <p className="text-[10px] uppercase tracking-[0.1em] font-medium text-ink-muted">Daily</p>
          <p className="font-serif text-[14px] font-medium text-ink mt-0.5 tabular-nums">
            ${summary.dailyBudget.toFixed(2)}
          </p>
        </div>
        <div className="bg-cream-warm/40 rounded-xl px-3 py-2.5 text-center">
          <p className="text-[10px] uppercase tracking-[0.1em] font-medium text-ink-muted">Projected</p>
          <p className={`font-serif text-[14px] font-medium mt-0.5 tabular-nums ${summary.projectedSpend > budget ? 'text-terracotta' : 'text-ink'}`}>
            ${Math.round(summary.projectedSpend)}
          </p>
        </div>
        <div className="bg-cream-warm/40 rounded-xl px-3 py-2.5 text-center">
          <p className="text-[10px] uppercase tracking-[0.1em] font-medium text-ink-muted">vs last</p>
          <p className={`font-serif text-[14px] font-medium mt-0.5 tabular-nums ${momTone}`}>
            {momLabel}
          </p>
        </div>
      </div>

      {/* Over-budget warning */}
      {overBudget && (
        <div className="rounded-xl bg-cream-warm border border-terracotta/30 px-3 py-2.5 mb-4 flex items-start gap-2.5">
          <AlertTriangle className="h-3.5 w-3.5 text-terracotta flex-shrink-0 mt-0.5" strokeWidth={1.8} />
          <p className="font-serif italic text-[12px] text-terracotta leading-[1.4]">
            ${(summary.spent - summary.budget).toFixed(2)} over your budget this month.
          </p>
        </div>
      )}

      {/* Near-limit warning */}
      {!overBudget && nearLimit && (
        <div className="rounded-xl bg-cream-warm/60 border border-sand px-3 py-2.5 mb-4 flex items-start gap-2.5">
          <AlertTriangle className="h-3.5 w-3.5 text-sand flex-shrink-0 mt-0.5" strokeWidth={1.8} />
          <p className="font-serif italic text-[12px] text-ink leading-[1.4]">
            ${summary.remaining.toFixed(2)} left for {summary.daysLeft} more day{summary.daysLeft !== 1 ? 's' : ''}.
          </p>
        </div>
      )}

      {/* Category breakdown */}
      {summary.categoryBreakdown.length > 0 && (
        <div className="mb-3">
          <p className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-3">
            By category
          </p>
          <div className="flex flex-col gap-2.5">
            {summary.categoryBreakdown.slice(0, 4).map((cat, i) => (
              <motion.div
                key={cat.category}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05, duration: 0.2 }}
              >
                <div className="flex justify-between mb-1">
                  <span className="font-serif text-[12px] text-ink capitalize">{cat.category}</span>
                  <span className="font-serif text-[12px] font-medium text-ink tabular-nums">
                    ${cat.spent.toFixed(2)}
                  </span>
                </div>
                <div className="h-1.5 bg-border-sand rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full rounded-full ${barClass}`}
                    initial={{ width: 0 }}
                    animate={{ width: `${cat.percentage}%` }}
                    transition={{ delay: i * 0.05 + 0.15, duration: 0.5, ease: 'easeOut' }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* MoM caption + details link */}
      <p className="font-serif italic text-[11px] text-ink-muted mb-2">
        Last month: ${lastMonthSpend.toFixed(0)}
      </p>
      <button
        type="button"
        onClick={onViewDetails}
        className="w-full text-center py-2 text-[12px] uppercase tracking-[0.1em] font-medium text-forest"
      >
        View all transactions →
      </button>
    </div>
  )
}
